import axios from '../../api/axiosInstance';

import { shapeResponse } from '../responseShaper';
import enforceResponsePolicy from '../policy/enforceResponsePolicy';
import { enforceResponseAdmissibility } from '../admissibility/enforceResponseAdmissibility';
import { debugLog } from '../logging/debugLog';

export default async function generateGovernedResponse(
  userInput,
  contextEnvelope,
  context = {},
  {
    effectiveUserName = 'Friend',
    activeIdentity = null,
    relationships = [],
    memoryFrame = null,
    activeEntity = null,
    arbitration = null,
    onError = null,
  } = {}
) {
  try {
    const response = await axios.post('/api/chat', {
      input: userInput,
      mode: 'full',
      context: contextEnvelope,
    });

    const rawReply = response.data.reply;

    const responseContext = {
      message: userInput,
      intent: context.intent,
      target: context.target,
      userName: effectiveUserName,
      memoryName: context.memoryName,
      activeIdentity,
      knownRelationships: context.knownRelationships || relationships,
      lastReferencedEntity: context.lastReferencedEntity ?? null,
      frame: context.frame ?? memoryFrame ?? null,
      emotionLevel: context.emotionLevel || 0,
      intentState: context.intentState || null,
      responseIntent: context.responseIntent || null,
      objectiveDecisionState: context.objectiveDecisionState || null,
      turnSignalArbitration: context.turnSignalArbitration || null,
      pendingQuestionState: context.pendingQuestionState || null,
      admissibilityState: context.admissibilityState || null,
      activeFrameState: context.activeFrameState || null,
      turnInterpretation: context.turnInterpretation || null,
      shapingPolicy: context.shapingPolicy || null,
    };

    const shapedResult = await shapeResponse(rawReply, responseContext);
    const shapedReply =
      typeof shapedResult === 'string'
        ? shapedResult
        : shapedResult?.response || rawReply;
    const responseShaperResult =
      typeof shapedResult === 'object' && shapedResult?.policyResult
        ? shapedResult.policyResult
        : null;
    const responseShaperTrace =
      typeof shapedResult === 'object' && Array.isArray(shapedResult?.trace)
        ? shapedResult.trace
        : [];

    // Final output boundary. Chat owns transport/orchestration only; it does
    // not inspect or interpret policy. The policy subsystem decides whether
    // the shaped candidate is admissible before anything reaches the UI.
    const governed = enforceResponsePolicy(
      shapedReply || rawReply,
      responseContext,
      { stage: 'chat_emit' }
    );

    const chatEmitInput = shapedReply || rawReply;
    const chatEmitOutput = governed.response || chatEmitInput;

    const responsePolicyResult = {
      originalCandidate: rawReply,
      shapedResponse: shapedReply || rawReply,
      emittedResponse: chatEmitOutput,
      trace: [
        ...responseShaperTrace,
        {
          id: 'chat_emit_policy',
          label: 'Chat Emit Policy',
          input: chatEmitInput,
          output: chatEmitOutput,
          changed: chatEmitInput !== chatEmitOutput,
          reason: 'Performs the final response-policy validation before transport to the chat UI.',
          authority:
            governed?.effectivePolicy?.authority ||
            governed?.validation?.policyAuthority ||
            null,
          decision: governed?.validation?.decision || null,
          violations: Array.isArray(governed?.validation?.violations)
            ? governed.validation.violations
            : [],
        },
      ],
      responseShaper: responseShaperResult,
      chatEmit: {
        ...governed,
        stage: 'chat_emit',
        inputResponse: chatEmitInput,
        outputResponse: chatEmitOutput,
      },
    };

    const aiReply = responsePolicyResult.emittedResponse;
    const admissibilityResult = enforceResponseAdmissibility({
      response: aiReply,
      activeEntity,
      arbitration,
      userName: effectiveUserName,
    });

    if (admissibilityResult.wasModified) {
      debugLog('🛡️ [Admissibility] Response constrained:', admissibilityResult);
    }

    const finalReply = admissibilityResult.finalReply || aiReply;
    const existingTrace = Array.isArray(responsePolicyResult.trace)
      ? responsePolicyResult.trace
      : [];

    const finalResponsePolicyResult = {
      ...responsePolicyResult,
      emittedResponse: finalReply,
      postAdmissibilityModified: admissibilityResult.wasModified === true,
      trace: [
        ...existingTrace,
        {
          id: 'response_admissibility',
          label: 'Response Admissibility',
          input: aiReply,
          output: finalReply,
          changed: aiReply !== finalReply,
          reason: admissibilityResult.wasModified
            ? 'The final admissibility guard constrained the response before it reached the chat window.'
            : 'The response passed the final admissibility guard without additional modification.',
          authority: 'runtime_admissibility',
          decision: admissibilityResult.wasModified ? 'TRANSFORM' : 'ALLOW',
          violations: [],
        },
      ],
    };

    return {
      response: finalReply,
      responsePolicyResult: finalResponsePolicyResult,
    };
  } catch (error) {
    if (typeof onError === 'function') {
      onError();
    }

    console.error('❌ Error getting AI response:', error);

    const fallbackResponse = "Sorry, I'm having trouble responding right now.";
    const fallbackAdmissibilityResult = enforceResponseAdmissibility({
      response: fallbackResponse,
      activeEntity,
      arbitration,
      userName: effectiveUserName,
    });

    if (fallbackAdmissibilityResult.wasModified) {
      debugLog(
        '🛡️ [Admissibility] Response constrained:',
        fallbackAdmissibilityResult
      );
    }

    return fallbackAdmissibilityResult.finalReply || fallbackResponse;
  }
}
